// Drawer d'édition des infos business (nom, type) - ouvert depuis l'onglet Informations générales

import React, { useEffect } from 'react';
import { Drawer, Form, Input, Select, Button, Space, message } from 'antd';
import { ShopOutlined } from '@ant-design/icons';
import { useUpdateBusiness } from '../../../hooks/useBusinesses';
import { Business, BusinessType, BUSINESS_TYPE_CONFIGS } from '../../../types/business';

const { Option } = Select;

interface EditBusinessInfoModalProps {
  open: boolean;
  business: Business | null;
  onClose: () => void;
  onSuccess: () => void;
}

const EditBusinessInfoModal: React.FC<EditBusinessInfoModalProps> = ({
  open,
  business,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const updateBusinessMutation = useUpdateBusiness();

  useEffect(() => {
    if (business && open) {
      form.setFieldsValue({
        businessName: business.name,
        businessType: business.type,
      });
    }
  }, [business, open, form]);

  const handleSubmit = async () => {
    if (!business) return;
    try {
      const values = await form.validateFields();
      await updateBusinessMutation.mutateAsync({
        id: business.id,
        name: values.businessName?.trim() || business.name,
        type: values.businessType || business.type,
      });
      message.success('Informations mises à jour');
      onSuccess();
      onClose();
      form.resetFields();
    } catch (error: any) {
      if (error.errorFields) return;
      message.error(error.message || 'Erreur lors de la mise à jour');
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  if (!business) return null;

  return (
    <Drawer
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ShopOutlined style={{ color: '#722ed1' }} />
          Modifier les infos du business
        </span>
      }
      open={open}
      onClose={handleCancel}
      destroyOnClose
      width={520}
      extra={
        <Space>
          <Button onClick={handleCancel}>Annuler</Button>
          <Button type="primary" loading={updateBusinessMutation.isPending} onClick={handleSubmit}>
            Enregistrer
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
        <Form.Item
          name="businessName"
          label="Nom du business"
          rules={[
            { required: true, message: 'Nom requis' },
            { min: 3, message: 'Minimum 3 caractères' },
            { max: 50, message: 'Maximum 50 caractères' },
          ]}
        >
          <Input placeholder="Ex: Institut Belle Époque" />
        </Form.Item>
        <Form.Item
          name="businessType"
          label="Type de business"
          rules={[{ required: true, message: 'Type requis' }]}
        >
          <Select placeholder="Sélectionner le type" optionLabelProp="label">
            {Object.entries(BUSINESS_TYPE_CONFIGS).map(([key, config]) => (
              <Option key={key} value={key} label={config.name}>
                <div>
                  <div style={{ fontWeight: 'bold' }}>{config.name}</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>{config.description}</div>
                </div>
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default EditBusinessInfoModal;
